import * as React from "react";
import { toast } from "sonner";

import { usePortal } from "@/features/portal/portal-context";
import {
  DEFAULT_FONT_ID,
  getTreatment,
  hexToOklch,
  normalizeSeed,
  recipeToLegacyHexes,
  treatmentsForPolarity,
  type LegacyHexes,
  type Polarity,
  type ThemeCandidate,
  type ThemeRecipe,
} from "@/data/theme-engine";
import { generateCandidates, applyDraftTreatment, liveClient, type BrandSource } from "@/commands";
import type { Theme } from "@/data/config";

/**
 * ThemeStudio — the self-contained state for the Theming v2 flow (candidates +
 * the 4 knobs). It lives HERE, not in portal-context, so Arron's save/publish/
 * discard/dirty logic stays untouched: the studio only ever writes the draft's 6
 * hexes through `onThemeChange` (the portal's existing seam) and keeps the RECIPE
 * (treatment/seed/polarity/font) draft-side (there is no backend field yet).
 *
 * The recipe is only surfaced to the live preview (as the v2 channel payload)
 * once the user has ACTIVELY engaged the studio for this theme — until then the
 * preview shows the saved 6 hexes verbatim (legacy), so opening a theme is
 * faithful rather than immediately re-generated.
 */
interface ThemeStudioValue {
  /** Current knob position (a sensible guess when a theme first opens). */
  recipe: ThemeRecipe;
  /** True once the user picked a candidate or moved a knob for this theme. */
  recipeActive: boolean;
  candidates: ThemeCandidate[];
  generating: boolean;
  /** Non-null when the last URL ingest returned a non-SUCCESS status. */
  ingestNote: string | null;
  generateFrom: (source: BrandSource) => Promise<void>;
  applyCandidate: (candidate: ThemeCandidate) => void;
  setSeed: (hex: string) => void;
  setPolarity: (polarity: Polarity) => void;
  setTreatment: (treatmentId: string) => void;
  setFont: (fontId: string) => void;
}

const ThemeStudioContext = React.createContext<ThemeStudioValue | null>(null);

/** Best-effort recipe guess from a stored theme's colours (seed = primary,
 *  polarity = ground lightness, treatment = the polarity default). */
function guessRecipe(theme: Theme): ThemeRecipe {
  let seed = "#7c3aed";
  try {
    seed = normalizeSeed(theme.primary);
  } catch {
    /* keep default */
  }
  let polarity: Polarity = "dark";
  try {
    polarity = hexToOklch(normalizeSeed(theme.background)).L >= 0.5 ? "light" : "dark";
  } catch {
    /* keep dark */
  }
  const treatmentId = treatmentsForPolarity(polarity)[0]?.id ?? "NIGHT";
  return { treatmentId, seed, polarity, fontId: DEFAULT_FONT_ID };
}

export function ThemeStudioProvider({ children }: { children: React.ReactNode }) {
  const { activeBrand, selectedThemeId, draft, onThemeChange } = usePortal();

  const [recipe, setRecipe] = React.useState<ThemeRecipe>(() => guessRecipe(draft.theme));
  const [recipeActive, setRecipeActive] = React.useState(false);
  const [candidates, setCandidates] = React.useState<ThemeCandidate[]>([]);
  const [generating, setGenerating] = React.useState(false);
  const [ingestNote, setIngestNote] = React.useState<string | null>(null);

  // Re-seed the studio whenever the open theme changes (switch theme / brand).
  // Guess from the newly-opened theme; nothing is "active" until the user acts.
  React.useEffect(() => {
    setRecipe(guessRecipe(draft.theme));
    setRecipeActive(false);
    setCandidates([]);
    setIngestNote(null);
    // Intentionally keyed on the theme identity, not on every draft colour edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedThemeId]);

  /** Push a recipe's derived hexes into the draft via the command seam. */
  const applyRecipe = React.useCallback(
    (next: ThemeRecipe) => {
      const { hexes } = applyDraftTreatment({ recipe: next });
      setRecipe(next);
      setRecipeActive(true);
      (Object.keys(hexes) as (keyof LegacyHexes)[]).forEach((k) => onThemeChange(k, hexes[k]));
    },
    [onThemeChange],
  );

  const applyCandidate = React.useCallback(
    (candidate: ThemeCandidate) => applyRecipe(candidate.recipe),
    [applyRecipe],
  );

  const setSeed = React.useCallback(
    (hex: string) => {
      try {
        applyRecipe({ ...recipe, seed: normalizeSeed(hex) });
      } catch {
        /* ignore partial/invalid hex while typing */
      }
    },
    [recipe, applyRecipe],
  );

  const setPolarity = React.useCallback(
    (polarity: Polarity) => {
      if (polarity === recipe.polarity) return;
      // Polarity is coupled to treatment today: pick this polarity's default
      // treatment unless the current one already matches.
      const current = getTreatment(recipe.treatmentId);
      const treatmentId =
        current.polarity === polarity ? recipe.treatmentId : treatmentsForPolarity(polarity)[0]?.id ?? recipe.treatmentId;
      applyRecipe({ ...recipe, polarity, treatmentId });
    },
    [recipe, applyRecipe],
  );

  const setTreatment = React.useCallback(
    (treatmentId: string) => {
      const t = getTreatment(treatmentId);
      applyRecipe({ ...recipe, treatmentId, polarity: t.polarity });
    },
    [recipe, applyRecipe],
  );

  const setFont = React.useCallback(
    (fontId: string) => applyRecipe({ ...recipe, fontId }),
    [recipe, applyRecipe],
  );

  const generateFrom = React.useCallback(
    async (source: BrandSource) => {
      if (!activeBrand) return;
      setGenerating(true);
      setIngestNote(null);
      try {
        const result = await generateCandidates(
          { brandId: activeBrand.id, source, fontId: recipe.fontId },
          { client: liveClient },
        );
        setCandidates(result.candidates);
        if (result.ingestMessage) setIngestNote(result.ingestMessage);
        if (result.candidates.length) applyCandidate(result.candidates[0]!);
      } catch (e) {
        toast.error(e instanceof Error && e.message ? e.message : "Couldn’t generate themes.");
      } finally {
        setGenerating(false);
      }
    },
    [activeBrand, recipe.fontId, applyCandidate],
  );

  const value: ThemeStudioValue = {
    recipe,
    recipeActive,
    candidates,
    generating,
    ingestNote,
    generateFrom,
    applyCandidate,
    setSeed,
    setPolarity,
    setTreatment,
    setFont,
  };

  return <ThemeStudioContext.Provider value={value}>{children}</ThemeStudioContext.Provider>;
}

export function useThemeStudio(): ThemeStudioValue {
  const ctx = React.useContext(ThemeStudioContext);
  if (!ctx) throw new Error("useThemeStudio must be used within a ThemeStudioProvider");
  return ctx;
}

/** Null-safe accessor for shared surfaces (e.g. LivePreview) that may render
 *  outside the studio (the Terminology page). Returns null when unprovided. */
export function useThemeStudioOptional(): ThemeStudioValue | null {
  return React.useContext(ThemeStudioContext);
}
